import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../redux/store";
import { motion, AnimatePresence } from "framer-motion";
import { X, Save, User, Building2, DollarSign, Calendar, FileText, AlertCircle } from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/badge";
import AdminHeader from "../../Headers/AdminHeader";
import { 
  createInvoice,
  clearInvoicesError,
  setInvoiceFormDirty
} from "../../redux/Slices/InvoiceSlice";
import { 
  InvoiceCreateDto, 
  InvoiceFormState, 
  InvoiceValidation,
  InvoiceStatusEnum,
  InvoiceCurrencyRef,
  InvoiceUserRef,
  InvoiceReceiverInformation,
  InvoiceSenderInformation
} from "../../modules/shared/model";
import UserDropdown from "../../components/ui/UserDropdown";
import CurrencyDropdown from "../../components/ui/CurrencyDropdown";

interface AddInvoiceProps {
  isOpen: boolean;
  onClose: () => void;
}

const AddInvoice: React.FC<AddInvoiceProps> = ({ isOpen, onClose }) => {
  const dispatch = useDispatch<AppDispatch>();
  
  // Redux state
  const { invoicesLoading, invoicesError } = useSelector((state: any) => state.invoices);

  // Form state
  const [formData, setFormData] = useState<InvoiceFormState>({
    basicSettings: {
      amount: 0,
      dueDate: "",
      issueDate: "",
      content: ""
    },
    receiverSettings: {
      receiverInformation: {
        name: "",
        email: "",
        address: "",
        firstName: "",
        lastName: "",
        vatNumber: ""
      },
      receiver: null
    },
    senderSettings: {
      senderInformation: {
        name: "",
        email: "",
        address: "",
        firstName: "",
        lastName: "",
        vatNumber: ""
      },
      sender: null
    },
    statusSettings: {
      status: InvoiceStatusEnum.Active
    },
    currencySettings: {
      currency: null
    }
  });

  // Validation state
  const [validation, setValidation] = useState<InvoiceValidation>({});
  const [isFormDirty, setIsFormDirty] = useState(false);

  // Handle user selection for receiver
  const handleReceiverSelect = (user: any) => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        receiverSettings: {
          ...prev.receiverSettings,
          receiver: user,
          receiverInformation: {
            ...prev.receiverSettings.receiverInformation,
            firstName: user.firstName || "",
            lastName: user.lastName || "",
            email: user.email || "",
            name: `${user.firstName || ""} ${user.lastName || ""}`.trim(),
            address: user.company?.address || user.address || "",
            vatNumber: user.company?.vatNumber || user.vatNumber || ""
          }
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        receiverSettings: {
          ...prev.receiverSettings,
          receiver: null
        }
      }));
    }
    setIsFormDirty(true);
    dispatch(setInvoiceFormDirty(true));
  };

  // Handle user selection for sender
  const handleSenderSelect = (user: any) => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        senderSettings: {
          ...prev.senderSettings,
          sender: user,
          senderInformation: {
            ...prev.senderSettings.senderInformation,
            firstName: user.firstName || "",
            lastName: user.lastName || "",
            email: user.email || "",
            name: `${user.firstName || ""} ${user.lastName || ""}`.trim(),
            address: user.company?.address || user.address || "",
            vatNumber: user.company?.vatNumber || user.vatNumber || ""
          }
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        senderSettings: {
          ...prev.senderSettings,
          sender: null
        }
      }));
    }
    setIsFormDirty(true);
    dispatch(setInvoiceFormDirty(true));
  };

  // Handle currency selection
  const handleCurrencySelect = (currency: any) => {
    setFormData(prev => ({
      ...prev,
      currencySettings: {
        ...prev.currencySettings,
        currency: currency
      }
    }));
    setIsFormDirty(true);
    dispatch(setInvoiceFormDirty(true));
  };

  // Handle form field changes
  const handleFieldChange = (field: string, value: any) => {
    setFormData(prev => {
      const newData = { ...prev };
      const keys = field.split(".");
      let current: any = newData;
      
      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      
      return newData;
    });
    
    setIsFormDirty(true);
    dispatch(setInvoiceFormDirty(true));
  };

  // Validate form
  const validateForm = (): boolean => {
    const newValidation: InvoiceValidation = {};

    // Basic validation
    if (!formData.basicSettings.amount || formData.basicSettings.amount <= 0) {
      newValidation.amount = "Amount must be greater than 0";
    }
    if (!formData.basicSettings.dueDate) {
      newValidation.dueDate = "Due date is required";
    }
    if (!formData.basicSettings.issueDate) {
      newValidation.issueDate = "Issue date is required";
    }

    // Receiver validation
    if (!formData.receiverSettings.receiverInformation.firstName.trim()) {
      newValidation.receiverInformation = { firstName: "Receiver first name is required" };
    }
    if (!formData.receiverSettings.receiverInformation.lastName.trim()) {
      newValidation.receiverInformation = { 
        ...newValidation.receiverInformation,
        lastName: "Receiver last name is required" 
      };
    }
    if (!formData.receiverSettings.receiverInformation.email.trim()) {
      newValidation.receiverInformation = { 
        ...newValidation.receiverInformation,
        email: "Receiver email is required" 
      };
    }

    // Sender validation (optional)
    // No validation required for sender as it's optional

    // Currency validation
    if (!formData.currencySettings.currency) {
      newValidation.currency = "Currency is required";
    }

    setValidation(newValidation);
    return Object.keys(newValidation).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const invoiceData: InvoiceCreateDto = {
      amount: formData.basicSettings.amount,
      dueDate: formData.basicSettings.dueDate,
      issueDate: formData.basicSettings.issueDate,
      content: formData.basicSettings.content,
      receiverInformation: formData.receiverSettings.receiverInformation,
      senderInformation: formData.senderSettings.senderInformation,
      receiver: formData.receiverSettings.receiver,
      sender: formData.senderSettings.sender,
      status: { id: formData.statusSettings.status },
      currency: formData.currencySettings.currency
    };

    try {
      await dispatch(createInvoice(invoiceData));
      // Reset form on success
      setFormData({
        basicSettings: { amount: 0, dueDate: "", issueDate: "", content: "" },
        receiverSettings: { 
          receiverInformation: { 
            name: "", email: "", address: "", firstName: "", lastName: "", vatNumber: "" 
          }, 
          receiver: null 
        },
        senderSettings: { 
          senderInformation: { 
            name: "", email: "", address: "", firstName: "", lastName: "", vatNumber: "" 
          }, 
          sender: null 
        },
        statusSettings: { status: InvoiceStatusEnum.Active },
        currencySettings: { currency: null }
      });
      setIsFormDirty(false);
      dispatch(setInvoiceFormDirty(false));
      onClose();
    } catch (error) {
      console.error("Error creating invoice:", error);
    }
  };

  // Handle modal close
  const handleClose = () => {
    if (isFormDirty) {
      const confirmed = window.confirm("You have unsaved changes. Are you sure you want to close?");
      if (!confirmed) return;
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ scale: 0.95 }}
          animate={{ scale: 1 }}
          exit={{ scale: 0.95 }}
          className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        >
          {/* Header */}
          <div className="p-6 border-b">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-xl font-semibold">Create New Invoice</h2>
                <p className="text-sm text-gray-500 mt-1">Fill in the details to create a new invoice</p>
              </div>
              <button
                onClick={handleClose}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-8">
            {/* Basic Information */}
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <FileText className="h-5 w-5 text-blue-600" />
                <h3 className="text-lg font-medium">Basic Information</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Amount *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.basicSettings.amount}
                    onChange={(e) => handleFieldChange("basicSettings.amount", parseFloat(e.target.value) || 0)}
                    className={`w-full px-3 py-2 border rounded-lg ${
                      validation.amount ? "border-red-500" : "border-gray-300"
                    }`}
                    placeholder="0.00"
                  />
                  {validation.amount && (
                    <p className="text-red-500 text-xs mt-1">{validation.amount}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Issue Date *
                  </label>
                  <input
                    type="date"
                    value={formData.basicSettings.issueDate}
                    onChange={(e) => handleFieldChange("basicSettings.issueDate", e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg ${
                      validation.issueDate ? "border-red-500" : "border-gray-300"
                    }`}
                  />
                  {validation.issueDate && (
                    <p className="text-red-500 text-xs mt-1">{validation.issueDate}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Due Date *
                  </label>
                  <input
                    type="date"
                    value={formData.basicSettings.dueDate}
                    onChange={(e) => handleFieldChange("basicSettings.dueDate", e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg ${
                      validation.dueDate ? "border-red-500" : "border-gray-300"
                    }`}
                  />
                  {validation.dueDate && (
                    <p className="text-red-500 text-xs mt-1">{validation.dueDate}</p>
                  )}
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Content
                </label>
                <textarea
                  value={formData.basicSettings.content}
                  onChange={(e) => handleFieldChange("basicSettings.content", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  rows={4}
                  placeholder="Invoice content/description..."
                />
              </div>
            </Card>

            {/* Receiver Information */}
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <User className="h-5 w-5 text-green-600" />
                <h3 className="text-lg font-medium">Receiver Information</h3>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select Receiver *
                  </label>
                  <UserDropdown
                    selectedUser={formData.receiverSettings.receiver ? {
                      id: formData.receiverSettings.receiver.id,
                      firstName: formData.receiverSettings.receiver.firstName || "",
                      lastName: formData.receiverSettings.receiver.lastName || "",
                      email: formData.receiverSettings.receiver.email || ""
                    } : null}
                    onUserSelect={handleReceiverSelect}
                    placeholder="Select a receiver..."
                    className="w-full"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name *
                    </label>
                    <input
                      type="text"
                      value={formData.receiverSettings.receiverInformation.firstName}
                      onChange={(e) => handleFieldChange("receiverSettings.receiverInformation.firstName", e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg ${
                        validation.receiverInformation?.firstName ? "border-red-500" : "border-gray-300"
                      }`}
                      placeholder="John"
                    />
                    {validation.receiverInformation?.firstName && (
                      <p className="text-red-500 text-xs mt-1">{validation.receiverInformation.firstName}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      value={formData.receiverSettings.receiverInformation.lastName}
                      onChange={(e) => handleFieldChange("receiverSettings.receiverInformation.lastName", e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg ${
                        validation.receiverInformation?.lastName ? "border-red-500" : "border-gray-300"
                      }`}
                      placeholder="Doe"
                    />
                    {validation.receiverInformation?.lastName && (
                      <p className="text-red-500 text-xs mt-1">{validation.receiverInformation.lastName}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email *
                    </label>
                    <input
                      type="email"
                      value={formData.receiverSettings.receiverInformation.email}
                      onChange={(e) => handleFieldChange("receiverSettings.receiverInformation.email", e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg ${
                        validation.receiverInformation?.email ? "border-red-500" : "border-gray-300"
                      }`}
                      placeholder="john@example.com"
                    />
                    {validation.receiverInformation?.email && (
                      <p className="text-red-500 text-xs mt-1">{validation.receiverInformation.email}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      VAT Number / Tax Code
                    </label>
                    <input
                      type="text"
                      value={formData.receiverSettings.receiverInformation.vatNumber}
                      onChange={(e) => handleFieldChange("receiverSettings.receiverInformation.vatNumber", e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder="VAT123456789"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Address
                    </label>
                    <textarea
                      value={formData.receiverSettings.receiverInformation.address}
                      onChange={(e) => handleFieldChange("receiverSettings.receiverInformation.address", e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      rows={2}
                      placeholder="123 Main St, City, State, Country"
                    />
                  </div>
                </div>
              </div>
            </Card>

            {/* Sender Information */}
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Building2 className="h-5 w-5 text-purple-600" />
                <h3 className="text-lg font-medium">Sender Information</h3>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select Sender (Optional)
                  </label>
                  <UserDropdown
                    selectedUser={formData.senderSettings.sender ? {
                      id: formData.senderSettings.sender.id,
                      firstName: formData.senderSettings.sender.firstName || "",
                      lastName: formData.senderSettings.sender.lastName || "",
                      email: formData.senderSettings.sender.email || ""
                    } : null}
                    onUserSelect={handleSenderSelect}
                    placeholder="Select a sender..."
                    className="w-full"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name
                    </label>
                    <input
                      type="text"
                      value={formData.senderSettings.senderInformation.firstName}
                      onChange={(e) => handleFieldChange("senderSettings.senderInformation.firstName", e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder="Company"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name
                    </label>
                    <input
                      type="text"
                      value={formData.senderSettings.senderInformation.lastName}
                      onChange={(e) => handleFieldChange("senderSettings.senderInformation.lastName", e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder="Name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      value={formData.senderSettings.senderInformation.email}
                      onChange={(e) => handleFieldChange("senderSettings.senderInformation.email", e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder="billing@company.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      VAT Number / Tax Code
                    </label>
                    <input
                      type="text"
                      value={formData.senderSettings.senderInformation.vatNumber}
                      onChange={(e) => handleFieldChange("senderSettings.senderInformation.vatNumber", e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder="VAT123456789"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Address
                    </label>
                    <textarea
                      value={formData.senderSettings.senderInformation.address}
                      onChange={(e) => handleFieldChange("senderSettings.senderInformation.address", e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      rows={2}
                      placeholder="456 Business Ave, City, State, Country"
                    />
                  </div>
                </div>
              </div>
            </Card>

            {/* Additional Settings */}
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <DollarSign className="h-5 w-5 text-yellow-600" />
                <h3 className="text-lg font-medium">Additional Settings</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Currency *
                  </label>
                  <CurrencyDropdown
                    selectedCurrency={formData.currencySettings.currency ? {
                      id: formData.currencySettings.currency.id,
                      name: formData.currencySettings.currency.name || "",
                      code: formData.currencySettings.currency.code || ""
                    } : null}
                    onCurrencySelect={handleCurrencySelect}
                    placeholder="Select a currency..."
                    className="w-full"
                  />
                  {validation.currency && (
                    <p className="text-red-500 text-xs mt-1">{validation.currency}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={formData.statusSettings.status}
                    onChange={(e) => handleFieldChange("statusSettings.status", parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value={InvoiceStatusEnum.Active}>Active</option>
                    <option value={InvoiceStatusEnum.Inactive}>Inactive</option>
                    <option value={InvoiceStatusEnum.Paid}>Paid</option>
                    <option value={InvoiceStatusEnum.Unpaid}>Unpaid</option>
                    <option value={InvoiceStatusEnum.Overdue}>Overdue</option>
                    <option value={InvoiceStatusEnum.Cancelled}>Cancelled</option>
                  </select>
                </div>
              </div>
            </Card>

            {/* Error Display */}
            {invoicesError && (
              <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg">
                <AlertCircle className="h-5 w-5 text-red-600" />
                <p className="text-red-600">{invoicesError}</p>
              </div>
            )}

            {/* Form Actions */}
            <div className="flex justify-end gap-3 pt-6 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={invoicesLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={invoicesLoading}
                className="flex items-center gap-2"
              >
                {invoicesLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Creating...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    Create Invoice
                  </>
                )}
              </Button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AddInvoice;
